import type { CardContentKind } from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  collectTransitiveComponents,
  type DecompositionMap,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.decomposition";
import type { ReviewEligibleDueUserCard } from "#use-due-user-cards-query";

/**
 * How long a review keeps its related cards "hot" (pushed behind every ready card). Longer than a
 * typical sitting, so within one sitting the hot cards form a second pass at the tail; after a
 * break the priming has faded and the queue is plain overdue order again.
 */
export const RECENT_EXPOSURE_WINDOW_MS = 30 * 60 * 1000;

/**
 * Subject text → the latest time (epoch ms) a card of that subject was reviewed. A reviewed word
 * is keyed by the word AND by each of its characters (see {@link stampExposure}), so a grapheme
 * card looks up its own glyph and a word card looks up the word plus its characters.
 */
export type RecentExposures = ReadonlyMap<string, number>;

function stampOne(exposures: Map<string, number>, subjectText: string, exposedAtMs: number): void {
  const existing = exposures.get(subjectText);
  if (existing === undefined || existing < exposedAtMs) {
    exposures.set(subjectText, exposedAtMs);
  }
}

/**
 * Record that `subjectText` was on screen at `exposedAtMs`, keeping the latest time per key. A
 * multi-character subject (a word) also stamps each character: a word card's reveal shows every
 * character it is made of.
 */
export function stampExposure(
  exposures: Map<string, number>,
  subjectText: string,
  exposedAtMs: number,
): void {
  stampOne(exposures, subjectText, exposedAtMs);
  for (const character of subjectText) {
    if (character !== subjectText) {
      stampOne(exposures, character, exposedAtMs);
    }
  }
}

function maxExposure(a: number | null, b: number | undefined): number | null {
  if (b === undefined) {
    return a;
  }
  return a === null || b > a ? b : a;
}

/**
 * - `ready`: nothing related was on screen within the window, and no due component stands ahead.
 * - `hot`: a related subject was reviewed within {@link RECENT_EXPOSURE_WINDOW_MS}; answering it
 *   now would test recency, not memory.
 * - `blocked`: a meaning card whose transitive decomposition component still has a due meaning
 *   card. Components are reviewed before the graphemes built from them, the same dependency the
 *   level generator enforces when it orders a level.
 */
export type ReviewQueueGroup = "blocked" | "hot" | "ready";

const GROUP_RANK = { blocked: 2, hot: 1, ready: 0 } as const satisfies Record<
  ReviewQueueGroup,
  number
>;

/**
 * Meaning cards first, then pronunciation, then word cards, then quizzes — the PRIMARY sort key,
 * above the group. Quizzes come last because learners come to reviews for their characters; a quiz
 * checks understanding a lesson already taught.
 * Kinds are served as contiguous phases: a meaning card surfacing after a run of pronunciation
 * cards (a hot compound, a blocked chain) is easily mistaken for another pronunciation card, so no
 * meaning card is ever served while a pronunciation card is ahead of it, however hot or blocked.
 */
const KIND_TIER = {
  "grapheme-meaning": 0,
  "grapheme-pronunciation": 1,
  quiz: 3,
  "word-characters-in-context": 2,
  "word-characters-in-sentence": 2,
  "word-pronunciation-in-sentence": 2,
} as const satisfies Record<CardContentKind, number>;

export type AnnotatedReviewQueueCard = {
  /** How many transitive components still have a due meaning card (blocked cards only). */
  blockedDepth: number;
  card: ReviewEligibleDueUserCard;
  /** Latest exposure of any related subject, or `null` when none is recorded. */
  exposedAtMs: number | null;
  group: ReviewQueueGroup;
};

function compareAnnotated(a: AnnotatedReviewQueueCard, b: AnnotatedReviewQueueCard): number {
  const tierDelta = KIND_TIER[a.card.cardContentKind] - KIND_TIER[b.card.cardContentKind];
  if (tierDelta !== 0) {
    return tierDelta;
  }
  const groupDelta = GROUP_RANK[a.group] - GROUP_RANK[b.group];
  if (groupDelta !== 0) {
    return groupDelta;
  }
  if (a.group === "hot") {
    // Least recently primed first: the card whose exposure is furthest behind us.
    const exposureDelta = (a.exposedAtMs ?? 0) - (b.exposedAtMs ?? 0);
    if (exposureDelta !== 0) {
      return exposureDelta;
    }
  }
  if (a.group === "blocked") {
    // Shallowest first, so a chain lists in dependency order (二 before 三 while 一 is due).
    const depthDelta = a.blockedDepth - b.blockedDepth;
    if (depthDelta !== 0) {
      return depthDelta;
    }
  }
  const dueDelta = a.card.dueMs - b.card.dueMs;
  if (dueDelta !== 0) {
    return dueDelta;
  }
  return a.card.userCardId < b.card.userCardId ? -1 : a.card.userCardId > b.card.userCardId ? 1 : 0;
}

export type OrderReviewQueueInput = {
  /** Glyph → direct components, from the static `grapheme_component` table. */
  decompositionMap: DecompositionMap;
  /** The eligible due cards, in any order. */
  dueCards: readonly ReviewEligibleDueUserCard[];
  /** Fetch-time clock, epoch ms. */
  now: number;
  recentExposures: RecentExposures;
  /**
   * A subject to treat as reviewed at `now` even though its review may not have landed yet: the
   * card the deck's flashcard queue is planning the next flashcard after.
   */
  virtualExposureSubject?: string;
};

/**
 * The review queue as an annotated, sorted list — the ordering behind {@link orderReviewQueue},
 * exposed so tests can assert on each card's group and exposure.
 *
 * The queue is recomputed from database state after every rating, so this is a priority function
 * over the current state rather than a fixed arrangement: serving the head never reshuffles the
 * rest, and no session state is needed. Per card:
 *
 * - **exposedAt** is the latest exposure among its related subjects. For a meaning card of G that
 *   is G itself, every transitive component of G, and any recently reviewed grapheme that has G as
 *   a component (a compound's reveal shows its components). For a pronunciation card it is G
 *   alone: the meaning card's back shows the pinyin and the pronunciation card's back shows the
 *   glyph, so either primes the other, while a component's review says nothing about G's
 *   reading. For a word card it is the word and each of its characters.
 * - **blocked** (meaning cards only): a transitive component still has a due meaning card.
 *
 * Sort: by kind first (every meaning card, then every pronunciation card, then every word card, then
 * every quiz), and
 * within a kind: ready cards (most overdue first), then hot cards (least recently primed first),
 * then blocked cards (fewest due components first, then most overdue). Hot and blocked cards are
 * still served when nothing else of their kind remains, so the queue never stalls.
 */
export function annotateReviewQueue({
  decompositionMap,
  dueCards,
  now,
  recentExposures,
  virtualExposureSubject,
}: OrderReviewQueueInput): AnnotatedReviewQueueCard[] {
  const exposures = new Map(recentExposures);
  if (virtualExposureSubject !== undefined) {
    stampExposure(exposures, virtualExposureSubject, now);
  }

  const closureCache = new Map<string, ReadonlySet<string>>();
  const transitiveComponentsOf = (glyph: string): ReadonlySet<string> => {
    let closure = closureCache.get(glyph);
    if (closure === undefined) {
      closure = collectTransitiveComponents(glyph, decompositionMap);
      closureCache.set(glyph, closure);
    }
    return closure;
  };

  // A compound's reveal shows its components, so a recently reviewed grapheme exposes everything
  // in its closure. Word keys carry several code points and have no decomposition, so they are
  // skipped here (their characters were stamped directly, see `stampExposure`).
  const componentExposures = new Map<string, number>();
  for (const [subjectText, exposedAtMs] of exposures) {
    const [firstCodePoint] = subjectText;
    if (firstCodePoint !== subjectText) {
      continue;
    }
    for (const component of transitiveComponentsOf(subjectText)) {
      stampOne(componentExposures, component, exposedAtMs);
    }
  }

  const dueMeaningGraphemes = new Set<string>();
  for (const card of dueCards) {
    if (card.cardContentKind === "grapheme-meaning") {
      dueMeaningGraphemes.add(card.subject.text);
    }
  }

  const hotSinceMs = now - RECENT_EXPOSURE_WINDOW_MS;
  const annotated = dueCards.map((card): AnnotatedReviewQueueCard => {
    const { text } = card.subject;
    let exposedAtMs = maxExposure(null, exposures.get(text));
    let blockedDepth = 0;
    if (card.cardContentKind === "grapheme-meaning") {
      exposedAtMs = maxExposure(exposedAtMs, componentExposures.get(text));
      for (const component of transitiveComponentsOf(text)) {
        exposedAtMs = maxExposure(exposedAtMs, exposures.get(component));
        if (dueMeaningGraphemes.has(component)) {
          blockedDepth += 1;
        }
      }
    } else if (card.subject.kind === "word") {
      for (const character of text) {
        exposedAtMs = maxExposure(exposedAtMs, exposures.get(character));
      }
    }
    const group: ReviewQueueGroup =
      blockedDepth > 0
        ? "blocked"
        : exposedAtMs !== null && exposedAtMs >= hotSinceMs
          ? "hot"
          : "ready";
    return { blockedDepth, card, exposedAtMs, group };
  });
  annotated.sort(compareAnnotated);
  return annotated;
}

/** The eligible due cards in serving order. See {@link annotateReviewQueue}. */
export function orderReviewQueue(input: OrderReviewQueueInput): ReviewEligibleDueUserCard[] {
  return annotateReviewQueue(input).map((entry) => entry.card);
}
